// src/components/EditNormalLoad.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "../styles/EditLoad.css";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { useTranslation } from "react-i18next";

/* ===================== Price / Currency ===================== */
const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
];

/* ===================== Helpers ===================== */
const toDateInput = (d) => {
  if (!d) return "";
  const s = typeof d === "string" ? d : new Date(d).toISOString();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

const toHourInput = (h) => {
  if (!h) return "";
  const m = String(h).match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
};

const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const emailsToOptions = (raw) => {
  if (!raw) return [];

  const arr = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(",")
        .map((x) => x.trim());

  return arr
    .map((email) => String(email || "").trim().toLowerCase())
    .filter((email) => email && isValidEmail(email))
    .slice(0, 3)
    .map((email) => ({ value: email, label: email }));
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const extractClientEmails = (clientObj) => {
  if (!clientObj || typeof clientObj !== "object") return [];

  const found = new Set();

  const addEmail = (value) => {
    const email = String(value || "").trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) return;
    found.add(email);
  };

  const contactsRaw = clientObj.contacts;
  let contacts = [];

  if (Array.isArray(contactsRaw)) {
    contacts = contactsRaw;
  } else if (typeof contactsRaw === "string") {
    try {
      const parsed = JSON.parse(contactsRaw);
      if (Array.isArray(parsed)) contacts = parsed;
    } catch {
      contacts = [];
    }
  }

  contacts.forEach((contact) => {
    addEmail(contact?.contactEmail);
  });

  return Array.from(found).map((email) => ({
    value: email,
    label: email,
  }));
};

const seedPlaceOptionsFromPrefill = (pf) => {
  if (!pf) return [];

  const pickups = pf.pickupPoints || pf.pickupLocations || [];
  const deliveries = pf.deliveryPoints || pf.deliveryLocations || [];
  const pts = [...pickups, ...deliveries];

  const opts = pts
    .map((p) => {
      const id = p.placeId ?? p.place?.id ?? p.location ?? "";
      const label = p.place?.placeName || p.location || "";
      return id && label ? { value: id, label, fullData: p.place ?? null } : null;
    })
    .filter(Boolean);

  return uniqBy(opts, (o) => String(o.value));
};

const getSessionCounter = (obj = {}) =>
  obj?.transportSession?.transportSessionNumber ?? obj?.tsUiCounter ?? obj?.transportsessionNumber ?? null;

const getLoadCounter = (obj = {}) => obj?.loadNumber ?? obj?.uiCounter ?? obj?.id ?? null;

const toId = (v) => (v == null ? "" : String(v));

const displayAuditEmail = (value) => {
  const email = String(value || "").trim();
  return email || "—";
};

/* === DEDUPE HELPERS === */
const norm = (s) => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
const keyOfPlace = (p) => [norm(p.placeName), norm(p.address), norm(p.postalCode), norm(p.city), norm(p.country)].join("|");

const scoreOfPlace = (p) =>
  (p.address ? 1 : 0) +
  (p.postalCode ? 1 : 0) +
  (p.city ? 1 : 0) +
  (p.country ? 1 : 0) +
  (p.gMapsLink ? 1 : 0) +
  (p.coordinates ? 1 : 0) +
  (p.contactPhone ? 1 : 0) +
  (p.workingHours ? 1 : 0);

/* ===== Render 2 linhas para as opções/valor do Location (label + city,country) ===== */
const renderPlaceOption = (opt, { context }) => {
  const city = opt.metaCity || opt.fullData?.city || "";
  const country = opt.metaCountry || opt.fullData?.country || "";
  const meta = [city, country].filter(Boolean).join(", ");
  return (
    <div className="el-place-option">
      <div className="el-place-option__label">{opt.label}</div>
      {meta && <div className={`el-place-option__meta ${context === "value" ? "is-value" : ""}`}>{meta}</div>}
    </div>
  );
};

const resolvePlaceSelectValue = (place, options) => {
  if (place?.placeId) {
    const byId = options.find((o) => String(o.value) === String(place.placeId));
    if (byId) return byId;
  }
  if (place?.location) {
    const byLabel = options.find((o) => o.label === place.location);
    if (byLabel) return byLabel;

    return {
      value: place.location,
      label: place.location,
      metaCity: place.city || "",
      metaCountry: place.country || "",
      fullData: {
        id: place.placeId ?? undefined,
        city: place.city || "",
        country: place.country || "",
      },
    };
  }
  return null;
};

/* ===================== Limits ===================== */
const MAX_LENGTH = 13.6;
const MAX_HEIGHT = 2.7;
const MAX_WIDTH = 2.5;
const MIN_LENGTH = 0.01;
const MAX_POINTS = 7;

function EditNormalLoad() {
  const { t } = useTranslation("edit_normal_load");

  const { loadId } = useParams();
  const navigate = useNavigate();
  const rLocation = useLocation();
  const prefill = rLocation.state?.prefill ?? rLocation.state?.load ?? null;

  console.log("[EditNormalLoad] state keys:", Object.keys(rLocation.state || {}));
  console.log("[EditNormalLoad] prefill keys:", Object.keys(prefill || {}));
  console.log("[EditNormalLoad] prefill pickup[0]:", prefill?.pickupPoints?.[0] || prefill?.pickupLocations?.[0]);
  console.log("[EditNormalLoad] prefill delivery[0]:", prefill?.deliveryPoints?.[0] || prefill?.deliveryLocations?.[0]);

  const mapPoint = (p = {}) => {
    const pl = p.place || {};

    return {
      // ✅ se vier nested, não perdes o nome
      location: p.location ?? pl.placeName ?? "",
      date: p.date ?? "",
      hour: p.hour ?? "",

      // 🆕 point-level dimensions
      length: p.length ?? "",
      width: p.width ?? "",
      height: p.height ?? "",

      // ✅ id também pode vir nested
      placeId: p.placeId ?? pl.id ?? null,

      // ✅ extended fields: flat OU nested
      address: p.address ?? pl.address ?? "",
      postalCode: p.postalCode ?? pl.postalCode ?? "",
      city: p.city ?? pl.city ?? "",
      country: p.country ?? pl.country ?? "",
      gMapsLink: p.gMapsLink ?? pl.gMapsLink ?? "",
      coordinates: p.coordinates ?? pl.coordinates ?? "",
      contactPhone: p.contactPhone ?? pl.contactPhone ?? "",

      reference: p.reference ?? "",
      loadingAccess: p.loadingAccess ?? null,

      handlingEquipment: p.handlingEquipment ?? null,
      handlingEquipmentOther: p.handlingEquipmentOther ?? "",

      showMoreFields: false,
    };
  };

  /* ===================== State ===================== */
  const [availableLength, setAvailableLength] = useState(MAX_LENGTH);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const [industryClients, setIndustryClients] = useState([]);
  const [carrierClients, setCarrierClients] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const [pickUpPlaceError, setPickUpPlaceError] = useState("");
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [invalidDeliveryIndexes, setInvalidDeliveryIndexes] = useState([]);

  const [existingDateHourConflicts, setExistingDateHourConflicts] = useState([]);
  const [conflictDateHours, setConflictDateHours] = useState(new Set());
  const [conflictError, setConflictError] = useState("");

  const [fetchedIndustryClientId, setFetchedIndustryClientId] = useState(null);
  const [fetchedCarrierClientId, setFetchedCarrierClientId] = useState(null);

  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");

  const dateRefs = useRef([]);
  const hourRefs = useRef([]);
  const deliveryDateRefs = useRef([]);
  const deliveryHourRefs = useRef([]);

  const [invalidHourFields, setInvalidHourFields] = useState([]);
  const [hourValidationError, setHourValidationError] = useState("");

  const [fieldValidationError, setFieldValidationError] = useState({ pickups: [], deliveries: [] });
  const [deliveryBeforePickupErrors, setDeliveryBeforePickupErrors] = useState({ date: [], hour: [] });

  const [clientTypeError, setClientTypeError] = useState("");
  const [clientSelectionError, setClientSelectionError] = useState("");
  const [transportCarrierError, setTransportCarrierError] = useState("");

  const [load, setLoad] = useState(() => prefill || null);
  const [length, setLength] = useState(() => prefill?.length ?? "");
  const [height, setHeight] = useState(() => prefill?.height ?? "");
  const [width, setWidth] = useState(() => prefill?.width ?? "");

  // 🆕 Weight (kg) (required)
  const [weight, setWeight] = useState(() => prefill?.weight ?? "");
  const [weightError, setWeightError] = useState("");

  // 🆕 Merchandise (required)
  const [merchandise, setMerchandise] = useState(() => prefill?.merchandise ?? "");
  const [merchandiseError, setMerchandiseError] = useState("");

  const [reference, setReference] = useState(() => prefill?.reference ?? "");

  const [price, setPrice] = useState(() => prefill?.price ?? "");
  const [priceCurrency, setPriceCurrency] = useState(() => prefill?.priceCurrency ?? "EUR");
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (optional, only when external carrier selected)
  const [price4subcontractedInput, setPrice4subcontractedInput] = useState(() =>
    prefill?.price4subcontrated ?? prefill?.price4subcontracted ?? ""
  );  
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");  

  const [notes4driver, setNotes4driver] = useState(() => prefill?.notes4driver ?? "");
  const [notes4driverError, setNotes4driverError] = useState("");

  // 🆕 Notes for subcontracted carrier
  const [notes4subcontracted, setNotes4subcontracted] = useState(() => prefill?.notes4subcontracted ?? "");
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  // 🆕 Subcontracted Emails (max 3, only when external carrier selected)
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState(() => {
    const raw =
      prefill?.subcontractedEmails ??
      prefill?.subcontractedEmail ??
      load?.subcontractedEmails ??
      load?.subcontractedEmail ??
      [];

    if (Array.isArray(raw)) {
      return raw
        .map((email) => {
          const e = String(email || "").trim().toLowerCase();
          return e && isValidEmail(e) ? { value: e, label: e } : null;
        })
        .filter(Boolean)
        .slice(0, 3);
    }

    if (typeof raw === "string" && raw.trim()) {
      const e = raw.trim().toLowerCase();
      return isValidEmail(e) ? [{ value: e, label: e }] : [];
    }

    return [];
  });

  // 🆕 Trailer Type (enum + OTHER)
  const [trailerType, setTrailerType] = useState(() => prefill?.trailerType ?? null);
  const [trailerTypeOther, setTrailerTypeOther] = useState(() => prefill?.trailerTypeOther ?? "");
  const [trailerTypeError, setTrailerTypeError] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState("");

  const [pickUpPlaces, setPickUpPlaces] = useState(() => {
    const src = prefill?.pickupPoints || prefill?.pickupLocations;
    return Array.isArray(src) && src.length ? src.map(mapPoint) : [
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        reference: "",
        loadingAccess: null,
        handlingEquipment: null,
        handlingEquipmentOther: "",
        showMoreFields: false,
      },
    ];
  });

  const [deliveryPlaces, setDeliveryPlaces] = useState(() => {
    const src = prefill?.deliveryPoints || prefill?.deliveryLocations;
    return Array.isArray(src) && src.length ? src.map(mapPoint) : [
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        reference: "",
        loadingAccess: null,
        handlingEquipment: null,
        handlingEquipmentOther: "",
        showMoreFields: false,
      },
    ];
  });

  const [clientType, setClientType] = useState(() => prefill?.clientType || "");
  const [industryClientId, setIndustryClientId] = useState(() => prefill?.industryClientId || "");
  const [carrierClientId, setCarrierClientId] = useState(() => prefill?.carrierClientId || "");
  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);

  const [carrierId, setCarrierId] = useState(() => toId(prefill?.carrierId));

  const makeOption = (id, label, fallbackPrefix = "ID") => (id ? { value: id, label: label || `${fallbackPrefix} ${id}` } : null);

  const [industryClientPrefill, setIndustryClientPrefill] = useState(() =>
    makeOption(prefill?.industryClientId, prefill?.industryClientName || prefill?.industryClient?.companyName, "Client")
  );

  const [carrierClientPrefill, setCarrierClientPrefill] = useState(() =>
    makeOption(prefill?.carrierClientId, prefill?.carrierClientName || prefill?.carrierClient?.companyName, "Client")
  );

  const [placeOptions, setPlaceOptions] = useState(() => seedPlaceOptionsFromPrefill(prefill));
  const [ready, setReady] = useState(() => !!prefill);

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };

  /* ===================== Refs sync ===================== */
  useEffect(() => {
    deliveryDateRefs.current = deliveryPlaces.map((_, i) => deliveryDateRefs.current[i] || null);
    deliveryHourRefs.current = deliveryPlaces.map((_, i) => deliveryHourRefs.current[i] || null);
  }, [deliveryPlaces]);

  /* ===================== Conflicts load ===================== */
  useEffect(() => {
    if (!load?.transportsessionId) return;
    (async () => {
      try {
        const res = await axios.get(`/pickupdeliveries/${load.transportsessionId}`);
        setExistingDateHourConflicts(res.data);
      } catch (err) {
        console.error("❌ Error fetching pickup/delivery conflicts:", err);
      }
    })();
  }, [load?.transportsessionId]);

  const conflictDebounce = useRef(null);
  useEffect(() => {
    if (conflictDebounce.current) clearTimeout(conflictDebounce.current);

    conflictDebounce.current = setTimeout(() => {
      const allNewPoints = [...pickUpPlaces, ...deliveryPlaces];
      const seenPairs = new Set();
      const msgs = [];

      for (const p of allNewPoints) {
        if (!p?.date || !p?.hour) continue;
        const candidates = (existingDateHourConflicts || []).filter(
          (e) => e.date === p.date && e.hour === p.hour && e.loadId !== load?.id
        );
        if (candidates.length) {
          seenPairs.add(`${p.date}|${p.hour}`);
          const m = candidates[0];
          msgs.push(t("errors.conflictWithExisting", { type: m.type, location: m.location, date: m.date, hour: m.hour }));
        }
      }

      setConflictDateHours(seenPairs);
      setConflictError(msgs.join("\n"));
    }, 150);

    return () => {
      if (conflictDebounce.current) clearTimeout(conflictDebounce.current);
    };
  }, [pickUpPlaces, deliveryPlaces, existingDateHourConflicts, load?.id, t]);

  /* ===================== Client preselect sync ===================== */
  useEffect(() => {
    if (clientType === "industry" && fetchedIndustryClientId && industryClients.length > 0) {
      setIndustryClientId(fetchedIndustryClientId);
    }
    if (clientType === "carrier" && fetchedCarrierClientId && carrierClients.length > 0) {
      setCarrierClientId(fetchedCarrierClientId);
    }
  }, [clientType, fetchedIndustryClientId, fetchedCarrierClientId, industryClients, carrierClients]);

  /* ===================== Boot fetch ===================== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsFetching(true);

        const [loadRes, placesRes, industriesRes, carriersRes] = await Promise.all([
          axios.get(`/loads/${loadId}/editloads`),
          axios.get("/places"),
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);

        if (cancelled) return;

        const loadData = loadRes.data;

        console.log("[EditNormalLoad] audit fields from API:", {
          createdByEmail: loadData?.createdByEmail,
          updatedByEmail: loadData?.updatedByEmail,
        });

        console.log("API pickup[0] handlingEquipment:", loadData?.pickupLocations?.[0]?.handlingEquipment);
        console.log("API pickup[0] handlingEquipmentOther:", loadData?.pickupLocations?.[0]?.handlingEquipmentOther);

        console.log("PREFILL pickup[0]", prefill?.pickupPoints?.[0] || prefill?.pickupLocations?.[0]);
        console.log("API editloads pickup[0]", loadData?.pickupLocations?.[0]);

        // ✅ sempre queremos a versão completa do API para preencher campos novos
        const apiPickups = Array.isArray(loadData.pickupLocations) ? loadData.pickupLocations.map(mapPoint) : [];
        const apiDeliveries = Array.isArray(loadData.deliveryLocations) ? loadData.deliveryLocations.map(mapPoint) : [];

        console.log("[EditNormalLoad] apiPickups dimensions:", apiPickups.map((p) => ({
          location: p.location,
          length: p.length,
          width: p.width,
          height: p.height,
        })));

        console.log("[EditNormalLoad] apiDeliveries dimensions:", apiDeliveries.map((p) => ({
          location: p.location,
          length: p.length,
          width: p.width,
          height: p.height,
        })));

        // Se NÃO veio prefill, mantém o comportamento antigo (hidrata tudo)
        if (!prefill) {
          setLoad(loadData);
          setLength(loadData.length ?? "");
          setHeight(loadData.height ?? "");
          setWidth(loadData.width ?? "");
          setWeight(loadData.weight ?? "");
          setMerchandise(loadData.merchandise ?? "");
          setReference(loadData.reference ?? "");
          setPrice(loadData.price ?? "");
          setPriceCurrency(loadData.priceCurrency ?? "EUR");
          setPrice4subcontractedInput(loadData.price4subcontrated ?? loadData.price4subcontracted ?? "");
          setNotes4driver(loadData.notes4driver ?? "");
          setNotes4subcontracted(loadData.notes4subcontracted ?? "");
          setSelectedClientContactEmails(
            emailsToOptions(loadData.clientContactEmails)
          );
          const subcontractedRaw =
            loadData.subcontractedEmails ??
            loadData.subcontractedEmail ??
            [];

          if (Array.isArray(subcontractedRaw)) {
            setSelectedSubcontractedEmails(
              subcontractedRaw
                .map((email) => {
                  const e = String(email || "").trim().toLowerCase();
                  return e && isValidEmail(e) ? { value: e, label: e } : null;
                })
                .filter(Boolean)
                .slice(0, 3)
            );
          } else if (typeof subcontractedRaw === "string" && subcontractedRaw.trim()) {
            const e = subcontractedRaw.trim().toLowerCase();
            setSelectedSubcontractedEmails(isValidEmail(e) ? [{ value: e, label: e }] : []);
          } else {
            setSelectedSubcontractedEmails([]);
          }
          setClientType(loadData.clientType || "");
          setTrailerType(loadData.trailerType ?? null);
          setTrailerTypeOther(loadData.trailerTypeOther ?? "");
          setIndustryClientId(loadData.industryClientId || "");
          setCarrierClientId(loadData.carrierClientId || "");
          setIndustryClientPrefill(makeOption(loadData.industryClientId, loadData.industryClient?.companyName, "Client"));
          setCarrierClientPrefill(makeOption(loadData.carrierClientId, loadData.carrierClient?.companyName, "Client"));

          // ✅ carrierId: external => id ; internal => "__internal__"
          if (loadData.carrierId != null) setCarrierId(toId(loadData.carrierId));
          else setCarrierId("__internal__");

          setPickUpPlaces(apiPickups);
          setDeliveryPlaces(apiDeliveries);

          setReady(true);
        } else {
          // ✅ Mesmo quando existe prefill, o API é a fonte de verdade.
          // O prefill serve só para abrir rápido; assim que o /editloads responde,
          // substituímos tudo pelo loadData atualizado.
          setLoad(loadData);

          setLength(loadData.length ?? "");
          setHeight(loadData.height ?? "");
          setWidth(loadData.width ?? "");
          setWeight(loadData.weight ?? "");
          setMerchandise(loadData.merchandise ?? "");
          setReference(loadData.reference ?? "");
          setPrice(loadData.price ?? "");
          setPriceCurrency(loadData.priceCurrency ?? "EUR");
          setPrice4subcontractedInput(
            loadData.price4subcontrated ?? loadData.price4subcontracted ?? ""
          );

          setNotes4driver(loadData.notes4driver ?? "");
          setNotes4subcontracted(loadData.notes4subcontracted ?? "");

          setClientType(loadData.clientType || "");
          setTrailerType(loadData.trailerType ?? null);
          setTrailerTypeOther(loadData.trailerTypeOther ?? "");

          setIndustryClientId(loadData.industryClientId || "");
          setCarrierClientId(loadData.carrierClientId || "");

          setFetchedIndustryClientId(loadData.industryClientId || null);
          setFetchedCarrierClientId(loadData.carrierClientId || null);

          setIndustryClientPrefill(
            makeOption(
              loadData.industryClientId,
              loadData.industryClient?.companyName || loadData.industryClientName,
              "Client"
            )
          );

          setCarrierClientPrefill(
            makeOption(
              loadData.carrierClientId,
              loadData.carrierClient?.companyName || loadData.carrierClientName,
              "Client"
            )
          );

          setSelectedClientContactEmails(
            emailsToOptions(loadData.clientContactEmails)
          );

          const subcontractedRaw =
            loadData.subcontractedEmails ??
            loadData.subcontractedEmail ??
            [];

          if (Array.isArray(subcontractedRaw)) {
            setSelectedSubcontractedEmails(
              subcontractedRaw
                .map((email) => {
                  const e = String(email || "").trim().toLowerCase();
                  return e && isValidEmail(e) ? { value: e, label: e } : null;
                })
                .filter(Boolean)
                .slice(0, 3)
            );
          } else if (typeof subcontractedRaw === "string" && subcontractedRaw.trim()) {
            const e = subcontractedRaw.trim().toLowerCase();
            setSelectedSubcontractedEmails(isValidEmail(e) ? [{ value: e, label: e }] : []);
          } else {
            setSelectedSubcontractedEmails([]);
          }

          // ✅ carrierId: external => id ; internal => "__internal__"
          if (loadData.carrierId != null) {
            setCarrierId(toId(loadData.carrierId));
          } else {
            setCarrierId("__internal__");
          }

          // ✅ Pontos também vêm sempre do API atualizado
          setPickUpPlaces(apiPickups);
          setDeliveryPlaces(apiDeliveries);

          setReady(true);
        }

        const fetchedPlaces = Array.isArray(placesRes.data) ? placesRes.data : [];
        const bestByKey = new Map();

        for (const p of fetchedPlaces) {
          const k = keyOfPlace(p);
          const s = scoreOfPlace(p);
          const prev = bestByKey.get(k);
          const isBetter = !prev || s > prev.score || new Date(p.updatedAt || 0) > new Date(prev.updatedAt || 0);
          if (isBetter) bestByKey.set(k, { p, score: s, updatedAt: p.updatedAt });
        }

        const seeded = seedPlaceOptionsFromPrefill(prefill)
          .map((o) => o.fullData)
          .filter(Boolean);

        for (const p of seeded) {
          const k = keyOfPlace(p);
          if (!bestByKey.has(k)) {
            bestByKey.set(k, { p, score: scoreOfPlace(p), updatedAt: p.updatedAt });
          }
        }

        const dedupedOptions = Array.from(bestByKey.values())
          .map(({ p }) => ({
            value: String(p.id),
            label: p.placeName,
            metaCity: p.city || "",
            metaCountry: p.country || "",
            fullData: p,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

        setPlaceOptions(dedupedOptions);
        setIndustryClients(industriesRes.data || []);
        setCarrierClients(carriersRes.data || []);
        setCarriers(carriersRes.data || []);

        console.log("industriesRes sample:", industriesRes.data?.[0]);
        console.log("carriersRes sample:", carriersRes.data?.[0]);

        const tsId = prefill?.transportsessionId || loadData?.transportsessionId;
        if (tsId) {
          fetchTransportSessionLoads(tsId, prefill || loadData);
          axios
            .get(`/pickupdeliveries/${tsId}`)
            .then((res) => !cancelled && setExistingDateHourConflicts(res.data))
            .catch(() => {});
        }
      } catch (err) {
        console.error("❌ Boot EditNormalLoad failed:", err);
        setLengthError(t("errors.fetchFailed"));
        if (!prefill) setReady(true);
      } finally {
        setIsFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadId, prefill, t]);

  /* ===================== Memo options ===================== */
  const carrierChipFromLoad = useMemo(() => {
    if (prefill?.carrierId) {
      return {
        value: String(prefill.carrierId),
        label: prefill?.carrierName || prefill?.carrier?.companyName || t("labels.carrierFallback"),
      };
    }
    if (load?.carrierId) {
      return {
        value: String(load.carrierId),
        label: load?.carrierName || load?.loadsAsCarrier?.companyName || t("labels.carrierFallback"),
      };
    }
    // ✅ internal fallback
    const internalLabel =
      (prefill?.carrierName || load?.carrierName || "").trim();

    if (internalLabel) {
      return { value: "__internal__", label: `${internalLabel} (internal)` };
    }

    return null;
  }, [
    prefill?.carrierId,
    prefill?.carrierName,
    prefill?.carrier?.companyName,
    load?.carrierId,
    load?.carrierName,
    load?.loadsAsCarrier,
    t,
  ]);

  const currentCarrierOption = useMemo(() => {
    const cid = toId(carrierId);
    if (!cid) return null;

    const found = carriers.find((c) => toId(c.id) === cid);
    if (found) return { value: toId(found.id), label: found.companyName };

    if (carrierChipFromLoad && toId(carrierChipFromLoad.value) === cid) return carrierChipFromLoad;

    return null;
  }, [carriers, carrierId, carrierChipFromLoad]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ tags sempre numa linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal quando há várias opções
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ altura/alinhamento consistente
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  // 🆕 company name (to compare internal vs external)
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;

        const raw =
          res.data?.company_name ??
          res.data?.companyName ??
          res.data?.company?.name ??
          res.data?.company?.companyName ??
          res.data?.user?.company_name ??
          "";

        setCompanyName(String(raw || "").trim());
      } catch (e) {
        setCompanyName("");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const loadingAccessOptions = useMemo(
    () => [
      { value: "SIDE", label: t("loadingAccess.side", { defaultValue: "Side" }) },
      { value: "REAR", label: t("loadingAccess.rear", { defaultValue: "Rear" }) },
      { value: "TOP", label: t("loadingAccess.top", { defaultValue: "Top" }) },
      { value: "DOCK", label: t("loadingAccess.dock", { defaultValue: "Dock" }) },
      { value: "ANY", label: t("loadingAccess.any", { defaultValue: "Any" }) },
    ],
    [t]
  );

  const handlingEquipmentOptions = useMemo(
    () => [
      { value: "FORKLIFT", label: t("handlingEquipment.forklift", { defaultValue: "Forklift" }) },
      { value: "PALLET_JACK", label: t("handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
      { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
      { value: "CRANE", label: t("handlingEquipment.crane", { defaultValue: "Crane" }) },
      { value: "LOADING_RAMP", label: t("handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
      { value: "TAIL_LIFT", label: t("handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
      { value: "HAND_LOAD", label: t("handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
      { value: "CONVEYOR", label: t("handlingEquipment.conveyor", { defaultValue: "Conveyor" }) },
      { value: "OTHER", label: t("handlingEquipment.other", { defaultValue: "Other" }) },
    ],
    [t]
  );

  // 🆕 Trailer type options (⚠️ manter values iguais ao enum do backend)
  const trailerTypeOptions = useMemo(
    () => [
      { value: "ANY", label: t("trailerType.any", { defaultValue: "Any" }) },
      { value: "TAUTLINER", label: t("trailerType.tautliner", { defaultValue: "Tautliner / Curtain" }) },
      { value: "REEFER", label: t("trailerType.reefer", { defaultValue: "Reefer / Frigo" }) },
      { value: "BOX", label: t("trailerType.box", { defaultValue: "Box" }) },
      { value: "FLATBED", label: t("trailerType.flatbed", { defaultValue: "Flatbed" }) },
      { value: "LOWBED", label: t("trailerType.lowbed", { defaultValue: "Lowbed" }) },
      { value: "TANKER", label: t("trailerType.tanker", { defaultValue: "Tanker" }) },
      { value: "TIPPER", label: t("trailerType.tipper", { defaultValue: "Tipper" }) },
      { value: "CONTAINER_CHASSIS", label: t("trailerType.containerChassis", { defaultValue: "Container chassis" }) },
      { value: "MEGA", label: t("trailerType.mega", { defaultValue: "Mega" }) },
      { value: "OTHER", label: t("trailerType.other", { defaultValue: "Other" }) },
    ],
    [t]
  );

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType) return [];

    if (clientType === "industry" && industryClientId) {
      const fullClient = (industryClients || []).find((c) => String(c.id) === String(industryClientId));
      return extractClientEmails(fullClient);
    }

    if (clientType === "carrier" && carrierClientId) {
      const fullClient = (carrierClients || []).find((c) => String(c.id) === String(carrierClientId));
      return extractClientEmails(fullClient);
    }

    return [];
  }, [clientType, industryClientId, carrierClientId, industryClients, carrierClients]);

  // ✅ In Edit mode, do NOT auto-overwrite saved clientContactEmails.
  // The selected emails must come from loadData.clientContactEmails.
  // Options can still come from the selected client's contact list.

  const carrierOptionsStable = useMemo(() => {
  const base = carriers.map((c) => ({ value: toId(c.id), label: c.companyName }));

  const internalLabel =
    String(companyName || "").trim() ||
    String(load?.carrierName || "").trim() ||
    String(prefill?.carrierName || "").trim() ||
    t("labels.internalCarrierFallback", { defaultValue: "Internal carrier" });

  const internalOpt = {
    value: "__internal__",
    label: `${internalLabel} (internal)`,
    isInternal: true,
  };

  const injected = currentCarrierOption ? [currentCarrierOption] : [];
    const withInternal = [internalOpt, ...injected, ...base];

    return uniqBy(withInternal, (o) => String(o.value));
  }, [carriers, currentCarrierOption, companyName, load?.carrierName, prefill?.carrierName, t]);

  // 🆕 selected carrier option
  const selectedTransportOptMemo = useMemo(() => {
    return (carrierOptionsStable || []).find((o) => String(o.value) === String(carrierId)) || null;
  }, [carrierOptionsStable, carrierId]);

  // 🆕 internal carrier flag
  const isInternalCarrierMemo = useMemo(() => {
    return selectedTransportOptMemo?.value === "__internal__";
  }, [selectedTransportOptMemo]);

  // 🆕 show subcontracted fields ONLY when external carrier selected
  const showNotes4subcontracted = useMemo(() => {
    if (!carrierId) return false;
    if (isInternalCarrierMemo) return false;

    const own = String(companyName || "").trim().toLowerCase();
    const sel = String(selectedTransportOptMemo?.label || "").trim().toLowerCase();

    if (!own || !sel) return true; // if can't compare, still treat as external
    return sel !== own;
  }, [carrierId, isInternalCarrierMemo, companyName, selectedTransportOptMemo]);

    const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted || !carrierId || String(carrierId) === "__internal__") return [];

    const fullCarrier = (carrierClients || []).find(
      (c) => String(c.id) === String(carrierId)
    );

    return extractClientEmails(fullCarrier);
  }, [showNotes4subcontracted, carrierId, carrierClients]);

  // 🧼 if user switches back to internal carrier, clear subcontracted fields
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");
      setSelectedSubcontractedEmails([]);
      setPrice4subcontractedInput("");
      setPrice4subcontractedError("");
    }
  }, [showNotes4subcontracted]);

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickUpPlaces);
  }, [pickUpPlaces]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPlaces);
  }, [deliveryPlaces]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  const hasAnyPointDimension = useMemo(() => {
    return [...pickUpPlaces, ...deliveryPlaces].some((point) => {
      return (
        String(point.length || "").trim() ||
        String(point.width || "").trim() ||
        String(point.height || "").trim()
      );
    });
  }, [pickUpPlaces, deliveryPlaces]);

  useEffect(() => {
    if (!hasAnyPointDimension) return;

    const nextLength =
      pointDimensionSummary.length > 0 ? pointDimensionSummary.length.toFixed(2) : "";

    const nextWidth =
      pointDimensionSummary.width > 0 ? pointDimensionSummary.width.toFixed(2) : "";

    const nextHeight =
      pointDimensionSummary.height > 0 ? pointDimensionSummary.height.toFixed(2) : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    hasAnyPointDimension,
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  /* ===================== Validations ===================== */
  const validateDeliveryAfterPickups = (deliveries = deliveryPlaces, pickups = pickUpPlaces) => {
    const pickupDateTimes = pickups
      .filter((p) => p.date && p.hour)
      .map((p) => new Date(`${p.date}T${p.hour}`));

    const errorIndexes = { date: [], hour: [] };

    deliveries.forEach((d, index) => {
      if (d.date && d.hour) {
        const deliveryDateTime = new Date(`${d.date}T${d.hour}`);
        for (const pickupDate of pickupDateTimes) {
          if (deliveryDateTime < pickupDate) {
            if (deliveryDateTime.toDateString() !== pickupDate.toDateString()) errorIndexes.date.push(index);
            else errorIndexes.hour.push(index);
          }
        }
      }
    });

    setDeliveryBeforePickupErrors(errorIndexes);
    return [...new Set([...errorIndexes.date, ...errorIndexes.hour])];
  };

  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const [year, month, day] = dateString.split("-").map(Number);
    if (!year || !month || !day) return false;
    const date = new Date(dateString);
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
  };

  const validateDateOnBlur = (value, index, type) => {
    if (!value || value.length < 10) return;

    const isInvalidFormat = !isValidDate(value);
    let errorMessage = "";

    if (isInvalidFormat) errorMessage = t("errors.invalidDateFormat");

    setDateFieldErrors((prev) => ({ ...prev, [`${type}-${index}`]: errorMessage }));

    if (!errorMessage) {
      setDateFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[`${type}-${index}`];
        return updated;
      });
    }
  };

  const handleLengthChange = (e) => {
    const value = e.target.value;
    setLength(value);
    const numericValue = parseFloat(value);

    if (Number.isNaN(numericValue) || numericValue < MIN_LENGTH) {
      setLengthError(t("errors.lengthMin", { min: MIN_LENGTH.toFixed(2) }));
    } else if (numericValue > availableLength) {
      setLengthError(t("errors.lengthMax", { max: availableLength.toFixed(2) }));
    } else {
      setLengthError("");
    }
  };

  const handleHeightChange = (e) => {
    const value = e.target.value;
    setHeight(value);
    const numericValue = parseFloat(value);

    if (Number.isNaN(numericValue) || numericValue < 0.01) {
      setHeightError(t("errors.heightMin"));
    } else if (numericValue > MAX_HEIGHT) {
      setHeightError(t("errors.heightMax", { max: MAX_HEIGHT.toFixed(2) }));
    } else {
      setHeightError("");
    }
  };

  const handleWidthChange = (e) => {
    const value = e.target.value;
    setWidth(value);
    const numericValue = parseFloat(value);

    if (Number.isNaN(numericValue) || numericValue < 0.01) {
      setWidthError(t("errors.widthMin"));
    } else if (numericValue > MAX_WIDTH) {
      setWidthError(t("errors.widthMax", { max: MAX_WIDTH.toFixed(2) }));
    } else {
      setWidthError("");
    }
  };

  const handleWeightChange = (e) => {
    const value = e.target.value;
    setWeight(value);

    if (value === "" || value == null) {
      setWeightError("");
      return;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue) || numericValue < 0) {
      setWeightError(t("errors.enterValidNumber", { defaultValue: "❌ Please enter a valid number." }));
    } else {
      setWeightError("");
    }
  };

  const handlePriceChange = (e) => {
    const v = e.target.value;
    setPrice(v);

    if (v === "" || v === null) {
      setPriceError("");
      return;
    }
    const n = Number(v);
    if (Number.isNaN(n) || n < 0) setPriceError(t("errors.priceInvalid"));
    else setPriceError("");
  };

  // 🆕 subcontracted price
  const handlePrice4subcontractedChange = (e) => {
    const v = e.target.value;
    setPrice4subcontractedInput(v);

    if (v === "" || v === null) {
      setPrice4subcontractedError("");
      return;
    }
    const n = Number(v);
    if (Number.isNaN(n) || n < 0) setPrice4subcontractedError(t("errors.priceInvalid"));
    else setPrice4subcontractedError("");
  };

  const fetchTransportSessionLoads = async (transportsessionId, currentLoad) => {
    try {
      const response = await axios.get(`/loads?transportsessionId=${transportsessionId}`);
      const sessionLoads = response.data;

      const otherLoads = sessionLoads.filter((l) => l.id !== currentLoad.id);
      const loadBearerIds = new Set(sessionLoads.filter((l) => l.loadBearer).map((l) => l.id));

      const superImposableMap = new Map();
      sessionLoads.forEach((l) => {
        const bearerId = parseInt(l.associatedLoadId || l.associatedLoadBearerId);
        const len = parseFloat(l.length) || 0;

        if (l.superImposable && bearerId) {
          if (!superImposableMap.has(bearerId)) superImposableMap.set(bearerId, 0);
          superImposableMap.set(bearerId, superImposableMap.get(bearerId) + len);
        }
      });

      const totalUsedLength = otherLoads.reduce((sum, l) => {
        if (l.superImposable && loadBearerIds.has(l.associatedLoadBearerId)) return sum;
        return sum + (parseFloat(l.length) || 0);
      }, 0);

      // Mantém cálculo (mesmo que não uses totalLoadBearerLength no final)
      sessionLoads
        .filter((l) => l.loadBearer)
        .reduce((sum, lb) => {
          let adjustedLength = parseFloat(lb.length) || 0;
          if (superImposableMap.has(parseInt(lb.id))) adjustedLength -= superImposableMap.get(parseInt(lb.id));
          return sum + adjustedLength;
        }, 0);

      const remainingLength = MAX_LENGTH - totalUsedLength;
      const availableSpace = remainingLength > 0 ? parseFloat(remainingLength.toFixed(2)) : 0;

      setAvailableLength(availableSpace);
    } catch (error) {
      console.error("❌ Error fetching transport session loads:", error);
    }
  };

  const handleHourInput = (e, index, field, isPickup = true) => {
    let value = e.target.value;

    if (/^\d{2}$/.test(value)) value = value + ":";

    if (!/^\d{0,2}:?\d{0,2}$/.test(value)) return;

    const full = /^\d{2}:\d{2}$/.test(value);
    const [hh = "", mm = ""] = value.split(":");
    const hour = Number(hh);
    const minute = Number(mm);

    const outOfRange =
      full &&
      (Number.isNaN(hour) || hour < 0 || hour > 23 || Number.isNaN(minute) || minute < 0 || minute > 59);

    const updated = isPickup ? [...pickUpPlaces] : [...deliveryPlaces];
    updated[index][field] = value;
    updated[index].hourError = !!outOfRange;

    if (isPickup) setPickUpPlaces(updated);
    else setDeliveryPlaces(updated);

    setInvalidHourFields((prev) => {
      const type = isPickup ? "pickup" : "delivery";
      const filtered = prev.filter((f) => !(f.type === type && f.index === index));
      const next = outOfRange ? [...filtered, { type, index }] : filtered;
      setHourValidationError(next.length ? t("errors.invalidHourFormat") : "");
      return next;
    });

    if (!isPickup) {
      const updatedInvalidIndexes = validateDeliveryAfterPickups(updated, pickUpPlaces);
      setInvalidDeliveryIndexes(updatedInvalidIndexes);
      setDeliveryPlaceError(updatedInvalidIndexes.length > 0 ? t("errors.deliveryBeforePickup") : "");
    }
  };

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) return t("errors.enterValidNumber");

    if (field === "length") {
      if (n > availableLength) {
        return t("errors.lengthMax", {
          max: availableLength.toFixed(2),
        });
      }

      return "";
    }

    if (field === "width") {
      if (n > MAX_WIDTH) {
        return t("errors.widthMax", {
          max: MAX_WIDTH.toFixed(2),
        });
      }

      return "";
    }

    if (field === "height") {
      if (n > MAX_HEIGHT) {
        return t("errors.heightMax", {
          max: MAX_HEIGHT.toFixed(2),
        });
      }

      return "";
    }

    return "";
  };

  const handlePickUpPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updatedPlaces = [...pickUpPlaces];
    updatedPlaces[index][field] = value;

    const hiddenFields = ["address", "postalCode", "city", "country", "gMapsLink", "coordinates", "contactPhone"];

    if (field === "location") updatedPlaces[index].locationError = false;
    if (field === "date") updatedPlaces[index].dateError = false;
    if (field === "hour") updatedPlaces[index].hourError = false;

    // 🆕 live point-level dimension validation
    if (["length", "width", "height"].includes(field)) {
      const dimensionError = getPointDimensionError(field, value);

      if (field === "length") {
        updatedPlaces[index].pointLengthError = !!dimensionError;
        updatedPlaces[index].pointLengthErrorMessage = dimensionError;
      }

      if (field === "width") {
        updatedPlaces[index].pointWidthError = !!dimensionError;
        updatedPlaces[index].pointWidthErrorMessage = dimensionError;
      }

      if (field === "height") {
        updatedPlaces[index].pointHeightError = !!dimensionError;
        updatedPlaces[index].pointHeightErrorMessage = dimensionError;
      }
    }

    if (hiddenFields.includes(field)) updatedPlaces[index][`${field}Error`] = false;

    if (updatedPlaces[index].showMoreFields === undefined) updatedPlaces[index].showMoreFields = false;

    setPickUpPlaces(updatedPlaces);

    if (field === "location" && value.trim() !== "") setPickUpPlaceError("");

    const updatedInvalidIndexes = validateDeliveryAfterPickups(deliveryPlaces, updatedPlaces);
    setInvalidDeliveryIndexes(updatedInvalidIndexes);
    setDeliveryPlaceError(updatedInvalidIndexes.length > 0 ? t("errors.deliveryBeforePickup") : "");
  };

  const handleDeliveryPlaceChange = (event, index, field) => {
    const value = event.target.value;
    const updatedPlaces = [...deliveryPlaces];
    updatedPlaces[index][field] = value;

    const hiddenFields = ["address", "postalCode", "city", "country", "gMapsLink", "coordinates", "contactPhone"];

    if (field === "location") updatedPlaces[index].locationError = false;
    if (field === "date") updatedPlaces[index].dateError = false;
    if (field === "hour") updatedPlaces[index].hourError = false;

    // 🆕 live point-level dimension validation
    if (["length", "width", "height"].includes(field)) {
      const dimensionError = getPointDimensionError(field, value);

      if (field === "length") {
        updatedPlaces[index].pointLengthError = !!dimensionError;
        updatedPlaces[index].pointLengthErrorMessage = dimensionError;
      }

      if (field === "width") {
        updatedPlaces[index].pointWidthError = !!dimensionError;
        updatedPlaces[index].pointWidthErrorMessage = dimensionError;
      }

      if (field === "height") {
        updatedPlaces[index].pointHeightError = !!dimensionError;
        updatedPlaces[index].pointHeightErrorMessage = dimensionError;
      }
    }

    if (hiddenFields.includes(field)) updatedPlaces[index][`${field}Error`] = false;

    if (updatedPlaces[index].showMoreFields === undefined) updatedPlaces[index].showMoreFields = false;

    setDeliveryPlaces(updatedPlaces);

    if (field === "location" && value.trim() !== "") setDeliveryPlaceError("");

    const updatedInvalidIndexes = validateDeliveryAfterPickups(updatedPlaces, pickUpPlaces);
    setInvalidDeliveryIndexes(updatedInvalidIndexes);
    setDeliveryPlaceError(updatedInvalidIndexes.length > 0 ? t("errors.deliveryBeforePickup") : "");
  };

  const handleLoadingAccessChange = (index, opt, isPickup = true) => {
    const value = opt?.value || null;

    if (isPickup) {
      setPickUpPlaces((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = {
          ...updated[index],
          loadingAccess: value,
          loadingAccessError: false,
        };
        return updated;
      });

      // se quiseres limpar a msg geral quando tudo está preenchido
      // (mantém simples: só limpa se já existir erro)
      if (pickUpPlaceError) setPickUpPlaceError("");
    } else {
      setDeliveryPlaces((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = {
          ...updated[index],
          loadingAccess: value,
          loadingAccessError: false,
        };
        return updated;
      });

      if (deliveryPlaceError) setDeliveryPlaceError("");
    }
  };

  const handleHandlingEquipmentChange = (index, opt, isPickup = true) => {
    const value = opt?.value || null;

    const apply = (prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      updated[index] = {
        ...updated[index],
        handlingEquipment: value,
        handlingEquipmentError: false,
        // se não for OTHER, limpamos o texto
        handlingEquipmentOther: value === "OTHER" ? (updated[index].handlingEquipmentOther || "") : "",
        handlingEquipmentOtherError: false,
      };

      return updated;
    };

    if (isPickup) setPickUpPlaces(apply);
    else setDeliveryPlaces(apply);
  };

  const handleHandlingEquipmentOtherChange = (index, e, isPickup = true) => {
    const v = e.target.value;

    const apply = (prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      updated[index] = {
        ...updated[index],
        handlingEquipmentOther: v,
        handlingEquipmentOtherError: false,
      };

      return updated;
    };

    if (isPickup) setPickUpPlaces(apply);
    else setDeliveryPlaces(apply);
  };

  // 🆕 Trailer Type handlers
  const handleTrailerTypeChange = (opt) => {
    const v = opt?.value || null;
    setTrailerType(v);
    setTrailerTypeError("");
    if (String(v || "").toUpperCase() !== "OTHER") {
      setTrailerTypeOther("");
      setTrailerTypeOtherError("");
    }
  };

  const handleTrailerTypeOtherInput = (e) => {
    const v = e.target.value;
    setTrailerTypeOther(v);
    if (trailerTypeOtherError && String(v || "").trim()) setTrailerTypeOtherError("");
  };

  const addPickUpPlace = () => {
    if (pickUpPlaces.length >= MAX_POINTS) return;
    setPickUpPlaces([
      ...pickUpPlaces,
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        reference: "",
        showMoreFields: false,
        loadingAccess: null,
        // 🆕 handling equipment (required)
        handlingEquipment: null,
        handlingEquipmentOther: "",
      },
    ]);
  };

  const removePickUpPlace = (index) => {
    const updatedPlaces = pickUpPlaces.filter((_, i) => i !== index);
    setPickUpPlaces(updatedPlaces);
  };

  const addDeliveryPlace = () => {
    if (deliveryPlaces.length >= MAX_POINTS) return;
    setDeliveryPlaces([
      ...deliveryPlaces,
      {
        location: "",
        date: "",
        hour: "",

        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",

        placeId: null,
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",
        showMoreFields: false,
       loadingAccess: null,
        // 🆕 handling equipment (required)
        handlingEquipment: null,
        handlingEquipmentOther: "",
      },
    ]);
  };

  const removeDeliveryPlace = (index) => {
    const updatedPlaces = deliveryPlaces.filter((_, i) => i !== index);
    setDeliveryPlaces(updatedPlaces);
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();

    setPickUpPlaceError("");
    setDeliveryPlaceError("");
    setHourValidationError("");
    setConflictError("");
    setClientTypeError("");
    setClientSelectionError("");
    setTransportCarrierError("");
    setNotes4driverError("");
    setNotes4subcontractedError("");
    setMerchandiseError("");
    setWeightError("");
    setPriceError("");
    setTrailerTypeError("");
    setTrailerTypeOtherError("");

    let hasError = false;

    if (notes4driver && notes4driver.length > 2000) {
      setNotes4driverError(t("errors.notesMax"));
      hasError = true;
    }

    if (notes4subcontracted && notes4subcontracted.length > 2000) {
      setNotes4subcontractedError(t("errors.notesMax"));
      hasError = true;
    }

    if (!String(merchandise || "").trim()) {
      setMerchandiseError(t("errors.merchandiseRequired", { defaultValue: "❌ Merchandise is required." }));
      hasError = true;
    }

    if (
      String(trailerType || "").toUpperCase() === "OTHER" &&
      !String(trailerTypeOther || "").trim()
    ) {
      setTrailerTypeOtherError(
        t("errors.trailerTypeOtherRequired", { defaultValue: "❌ Please specify the trailer type." })
      );
      hasError = true;
    }

    if (weight !== "" && weight != null) {
      const n = Number(weight);

      if (Number.isNaN(n) || n < 0) {
        setWeightError(
          t("errors.enterValidNumber", {
            defaultValue: "❌ Please enter a valid number.",
          })
        );
        hasError = true;
      }
    }

    const priceValue =
      String(price ?? "").trim() === ""
        ? null
        : Number(price);

    if (priceValue === null) {
      setPriceError(
        t("errors.priceRequired", {
          defaultValue: "❌ Price is required.",
        })
      );
      hasError = true;
    } else if (!Number.isFinite(priceValue) || priceValue < 0) {
      setPriceError(
        t("errors.priceInvalid", {
          defaultValue: "❌ Price must be a valid non-negative number.",
        })
      );
      hasError = true;
    } else {
      setPriceError("");
    }

    if (!clientType) {
      setClientTypeError(
        t("errors.clientTypeRequired", {
          defaultValue: "❌ Client type is required.",
        })
      );
      hasError = true;
    } else {
      setClientTypeError("");
    }

    const resolvedClientId =
      clientType === "industry"
        ? industryClientId
        : clientType === "carrier"
          ? carrierClientId
          : null;

    if (!resolvedClientId) {
      setClientSelectionError(
        t("errors.clientRequired", {
          defaultValue: "❌ Client is required.",
        })
      );
      hasError = true;
    } else {
      setClientSelectionError("");
    }

    if (pickUpPlaces.length === 0) {
      setPickUpPlaceError(t("errors.atLeastOnePickup"));
      hasError = true;
    }
    if (deliveryPlaces.length === 0) {
      setDeliveryPlaceError(t("errors.atLeastOneDelivery"));
      hasError = true;
    }

    const validatePlaces = (places) =>
      places.map((place) => {
        const locationError = !place.location?.trim();
        const dateError = !place.date;
        const hourError = !place.hour;

        const addressError = !place.address?.trim();
        const postalCodeError = !place.postalCode?.trim();
        const cityError = !place.city?.trim();
        const countryError = !place.country?.trim();
        const contactPhoneError = !place.contactPhone?.trim();
        const gMapsLinkError = !place.gMapsLink?.trim();
        const coordinatesError = !place.coordinates?.trim();

        const hasAnyError =
          locationError ||
          dateError ||
          hourError ||
          addressError ||
          postalCodeError ||
          cityError ||
          countryError ||
          contactPhoneError ||
          gMapsLinkError ||
          coordinatesError;

        if (hasAnyError) hasError = true;

        return {
          ...place,
          locationError,
          dateError,
          hourError,
          addressError,
          postalCodeError: postalCodeError,
          cityError,
          countryError,
          contactPhoneError,
          gMapsLinkError,
          coordinatesError,
          showMoreFields: hasAnyError,
        };
      });

    // ⚠️ fix typo var name to avoid ReferenceError
    // (mantém comportamento e evita crash)
    const validatePlacesSafe = (places) =>
      places.map((place) => {
        const locationError = !place.location?.trim();

        const pointLengthErrorMessage =
          String(place.length ?? "").trim() !== ""
            ? getPointDimensionError("length", place.length)
            : "";

        const pointWidthErrorMessage =
          String(place.width ?? "").trim() !== ""
            ? getPointDimensionError("width", place.width)
            : "";

        const pointHeightErrorMessage =
          String(place.height ?? "").trim() !== ""
            ? getPointDimensionError("height", place.height)
            : "";

        const handlingEquipmentOtherError =
          String(place.handlingEquipment || "").toUpperCase() === "OTHER" &&
          !String(place.handlingEquipmentOther || "").trim();

        const hourError =
          String(place.hour || "").trim() !== "" &&
          Boolean(place.hourError);

        const hasAnyError =
          locationError ||
          hourError ||
          Boolean(pointLengthErrorMessage) ||
          Boolean(pointWidthErrorMessage) ||
          Boolean(pointHeightErrorMessage) ||
          handlingEquipmentOtherError;

        if (hasAnyError) hasError = true;

        return {
          ...place,
          locationError,
          dateError: false,
          hourError,
          loadingAccessError: false,
          handlingEquipmentError: false,
          handlingEquipmentOtherError,

          pointLengthError: Boolean(pointLengthErrorMessage),
          pointWidthError: Boolean(pointWidthErrorMessage),
          pointHeightError: Boolean(pointHeightErrorMessage),

          pointLengthErrorMessage,
          pointWidthErrorMessage,
          pointHeightErrorMessage,

          addressError: false,
          postalCodeError: false,
          cityError: false,
          countryError: false,
          contactPhoneError: false,
          gMapsLinkError: false,
          coordinatesError: false,
          showMoreFields: place.showMoreFields,
        };
      });

    const updatedPickups = validatePlacesSafe(pickUpPlaces);
    const updatedDeliveries = validatePlacesSafe(deliveryPlaces);

    setPickUpPlaces(updatedPickups);
    setDeliveryPlaces(updatedDeliveries);

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = updatedPickups.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = updatedDeliveries.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const loadLengthValue = Number(length);

    const allPickupLengthsProvided =
      updatedPickups.length > 0 &&
      updatedPickups.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    const allDeliveryLengthsProvided =
      updatedDeliveries.length > 0 &&
      updatedDeliveries.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      Number.isFinite(loadLengthValue) &&
      loadLengthValue > 0 &&
      allPickupLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(loadLengthValue)
    ) {
      hasError = true;

      setPickUpPlaceError(
        t("errors.pickupLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Pick-Up length must equal the Load length.",
        })
      );

      setPickUpPlaces((prev) =>
        prev.map((p) => ({
          ...p,
          pointLengthError: true,
        }))
      );
    }

    if (
      Number.isFinite(loadLengthValue) &&
      loadLengthValue > 0 &&
      allDeliveryLengthsProvided &&
      toCents(deliveryLengthTotal) !== toCents(loadLengthValue)
    ) {
      hasError = true;

      setDeliveryPlaceError(
        t("errors.deliveryLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Delivery length must equal the Load length.",
        })
      );

      setDeliveryPlaces((prev) =>
        prev.map((p) => ({
          ...p,
          pointLengthError: true,
        }))
      );
    }

    if (updatedPickups.some((p) => p.locationError)) {
      setPickUpPlaceError(t("errors.locationRequired"));
    }

    if (updatedDeliveries.some((p) => p.locationError)) {
      setDeliveryPlaceError(t("errors.locationRequired"));
    }

    if (invalidHourFields.length > 0) {
      setHourValidationError(t("errors.invalidHourFormat"));
      hasError = true;
    }

    if (conflictDateHours.size > 0) {
      setConflictError(t("errors.conflictGeneric"));
      hasError = true;
    }

    const invalidIndexes = validateDeliveryAfterPickups();
    if (invalidIndexes.length > 0) {
      setInvalidDeliveryIndexes(invalidIndexes);
      setDeliveryPlaceError(t("errors.deliveryBeforePickup"));
      hasError = true;
    } else {
      setInvalidDeliveryIndexes([]);
    }

    if (showNotes4subcontracted && price4subcontractedInput !== "" && price4subcontractedInput != null) {
      const n = Number(price4subcontractedInput);
      if (Number.isNaN(n) || n < 0) {
        setPrice4subcontractedError(t("errors.priceInvalid"));
        hasError = true;
      }
    }

    if (hasError) return;

    const cleanReference = typeof reference === "string" ? (reference.trim() || null) : null;

    const cleanNotes4driver = typeof notes4driver === "string" ? (notes4driver.trim() || null) : null;
    const cleanNotes4subcontracted =
      typeof notes4subcontracted === "string" ? (notes4subcontracted.trim() || null) : null;
    const cleanMerchandise = typeof merchandise === "string" ? merchandise.trim() : "";

    const cleanPrice4subcontracted =
      showNotes4subcontracted && price4subcontractedInput !== "" && price4subcontractedInput != null
        ? Number(price4subcontractedInput)
        : null;
    
    const firstPickupFilled = updatedPickups.find((p) => String(p.location || "").trim() !== "") || null;

    // ✅ carrierId: "__internal__" => null (internal); else -> number (external)
    const cleanCarrierId =
      !carrierId || String(carrierId) === "__internal__"
        ? null
        : Number.isFinite(Number(carrierId))
        ? Number(carrierId)
        : null;

    const payload = {
      reference: cleanReference,
      loadingAccess: firstPickupFilled?.loadingAccess || null,
      length:
        String(length ?? "").trim() === "" || !Number.isFinite(Number(length))
          ? null
          : Number(length),
      height:
        String(height ?? "").trim() === "" || !Number.isFinite(Number(height))
          ? null
          : Number(height),
      width:
        String(width ?? "").trim() === "" || !Number.isFinite(Number(width))
          ? null
          : Number(width),
      weight:
        String(weight ?? "").trim() === "" || !Number.isFinite(Number(weight))
          ? null
          : Number(weight),
      merchandise: cleanMerchandise,
      trailerType: trailerType ? String(trailerType).trim().toUpperCase() : null,
      trailerTypeOther:
        String(trailerType || "").toUpperCase() === "OTHER"
          ? (String(trailerTypeOther || "").trim() || null)
          : null,
      price: priceValue,
      price4subcontrated: cleanPrice4subcontracted,
      priceCurrency: priceCurrency || "EUR",
      specialLoad: load?.specialLoad || false,
      superImposable: false,
      loadBearer: false,
      notes4driver: cleanNotes4driver,
      notes4subcontracted: cleanNotes4subcontracted,

      // ✅ IMPORTANT: send selected client contact emails to backend
      clientContactEmails: selectedClientContactEmails
        .map((opt) => String(opt?.value || "").trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 3),

      subcontractedEmails: showNotes4subcontracted
        ? selectedSubcontractedEmails
            .map((opt) => String(opt?.value || "").trim().toLowerCase())
            .filter(Boolean)
            .slice(0, 3)
        : [],

      clientType: clientType || null,
      pickupLocations: updatedPickups
        .filter((p) => p.location.trim() !== "")
        .map((p) => ({
          reference: String(p.reference || "").trim() || null,
          loadingAccess: p.loadingAccess || null,
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          placeId: p.placeId || null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),
      deliveryLocations: updatedDeliveries
        .filter((p) => p.location.trim() !== "")
        .map((p) => ({
          reference: String(p.reference || "").trim() || null,
          location: p.location,
          date: p.date || null,
          hour: p.hour || null,

          // 🆕 point-level dimensions
          length: p.length === "" ? null : Number(p.length),
          width: p.width === "" ? null : Number(p.width),
          height: p.height === "" ? null : Number(p.height),

          placeId: p.placeId || null,
          address: p.address || "",
          postalCode: p.postalCode || "",
          city: p.city || "",
          country: p.country || "",
          gMapsLink: p.gMapsLink || "",
          coordinates: p.coordinates || "",
          contactPhone: p.contactPhone || "",
          loadingAccess: p.loadingAccess || null,
          handlingEquipment: p.handlingEquipment || null,
          handlingEquipmentOther:
            String(p.handlingEquipment || "").toUpperCase() === "OTHER"
              ? (String(p.handlingEquipmentOther || "").trim() || null)
              : null,
        })),
      pickupPoints: updatedPickups.map((p) => ({
        reference: String(p.reference || "").trim() || null,
        loadingAccess: p.loadingAccess || null,
        location: p.location,
        date: p.date,
        hour: p.hour || null,

        // 🆕 point-level dimensions
        length: p.length === "" ? null : Number(p.length),
        width: p.width === "" ? null : Number(p.width),
        height: p.height === "" ? null : Number(p.height),

        address: p.address || "",
        postalCode: p.postalCode || "",
        city: p.city || "",
        country: p.country || "",
        gMapsLink: p.gMapsLink || "",
        coordinates: p.coordinates || "",
        contactPhone: p.contactPhone || "",
        handlingEquipment: p.handlingEquipment || null,
        handlingEquipmentOther:
          String(p.handlingEquipment || "").toUpperCase() === "OTHER"
            ? (String(p.handlingEquipmentOther || "").trim() || null)
            : null,
      })),
      deliveryPoints: updatedDeliveries.map((p) => ({
        reference: String(p.reference || "").trim() || null,
        loadingAccess: p.loadingAccess || null,
        location: p.location,
        date: p.date,
        hour: p.hour || null,

        // 🆕 point-level dimensions
        length: p.length === "" ? null : Number(p.length),
        width: p.width === "" ? null : Number(p.width),
        height: p.height === "" ? null : Number(p.height),

        address: p.address || "",
        postalCode: p.postalCode || "",
        city: p.city || "",
        country: p.country || "",
        gMapsLink: p.gMapsLink || "",
        coordinates: p.coordinates || "",
        contactPhone: p.contactPhone || "",
        handlingEquipment: p.handlingEquipment || null,
        handlingEquipmentOther:
          String(p.handlingEquipment || "").toUpperCase() === "OTHER"
            ? (String(p.handlingEquipmentOther || "").trim() || null)
            : null,
      })),
      industryClientId:
        clientType === "industry" ? (industryClientId || null) : null,
      carrierClientId:
        clientType === "carrier" ? (carrierClientId || null) : null,
      carrierId: cleanCarrierId,
    };

    try {
      setLoading(true);
      await axios.put(`/loads/${loadId}`, payload);
      setLoading(false);
      setSuccessPopupOpen(true);
    } catch (error) {
      console.error("Error updating Normal Load:", error);
      setLengthError(t("errors.updateFailed"));
      setLoading(false);
    }
  };

  /* ===================== Early render guard ===================== */
  if (!ready) return null;

  return (
    <div className="edit-load-container">
      <div className="edit-load-back-arrow" onClick={() => navigate("/loads")}>
        ← {t("actions.back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-load-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-load-success-title"
        >
          <div className="edit-load-popupModal">
            <div className="edit-load-popupIconWrap">
              <FaCheckCircle className="edit-load-popupIcon" />
            </div>

            <h2
              id="edit-load-success-title"
              className="edit-load-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-load-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-load-popupActions">
              <button
                type="button"
                className="edit-load-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-load-title">{t("title")}</h1>

      <div className="edit-load-card">
        <form className="edit-load-form" onSubmit={handleSaveChanges}>
          {/* Line 1: Transport Session • Load • Audit */}
          <div className="edit-load-top-meta-row">
            <div className="edit-load-row el-kv-row edit-load-top-left-meta">
              <span
                className="el-kv-pair"
                title={
                  getSessionCounter(load) != null
                    ? t("tooltips.transportSessionNumber", { n: getSessionCounter(load) })
                    : load?.transportsessionId
                    ? t("tooltips.transportSessionId", { id: load.transportsessionId })
                    : ""
                }
              >
                <span className="el-kv__key">{t("labels.transportSession")}:</span>
                <span className="el-kv__value">{getSessionCounter(load) ?? "—"}</span>
              </span>

              <span className="el-kv__sep">•</span>

              <span
                className="el-kv-pair"
                title={
                  getLoadCounter(load) != null
                    ? t("tooltips.loadNumber", { n: getLoadCounter(load) })
                    : load?.id
                    ? t("tooltips.loadId", { id: load.id })
                    : ""
                }
              >
                <span className="el-kv__key">{t("labels.load")}:</span>
                <span className="el-kv__value">{getLoadCounter(load) ?? "—"}</span>
              </span>
            </div>

            <div className="edit-load-audit-inline">
              <span
                className="edit-load-audit-chip"
                title={displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
              >
                <span className="edit-load-audit-chip-label">
                  {t("labels.createdByEmail", { defaultValue: "Created by" })}
                </span>
                <span className="edit-load-audit-chip-value">
                  {displayAuditEmail(load?.createdByEmail ?? prefill?.createdByEmail)}
                </span>
              </span>

              <span
                className="edit-load-audit-chip"
                title={displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
              >
                <span className="edit-load-audit-chip-label">
                  {t("labels.updatedByEmail", { defaultValue: "Updated by" })}
                </span>
                <span className="edit-load-audit-chip-value">
                  {displayAuditEmail(load?.updatedByEmail ?? prefill?.updatedByEmail)}
                </span>
              </span>
            </div>
          </div>

          <div className="edit-load-columns">
            {/* Column 1 - Pick-Up Points */}
            <div className="edit-load-column">
              <h3 className="edit-load-column-title">{t("columns.loadingPoints")}</h3>
              {pickUpPlaceError && <p className="edit-load-error">{pickUpPlaceError}</p>}

              {pickUpPlaces.map((place, index) => (
                <div key={index} className="edit-load-multi-location-input">
                  <div className="edit-load-label-row">
                    <label className="edit-load-points-label">{t("labels.pickupPlace")}*</label>
                    {pickUpPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-load-delete-btn"
                        onClick={() => removePickUpPlace(index)}
                        title={t("tooltips.removePickup")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-load-input-row">
                    <div className="edit-load-select-wrapper">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        options={placeOptions}
                        placeholder={t("place.selectOrType")}
                        value={resolvePlaceSelectValue(place, placeOptions)}
                        formatOptionLabel={renderPlaceOption}
                        onChange={(selectedOption) => {
                          const updated = [...pickUpPlaces];

                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: null, // 🆕 reset (como CreateLoad)
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                              handlingEquipmentError: false,
                              handlingEquipmentOtherError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: String(p.id),
                              location: p.placeName,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              locationError: false,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: null, // 🆕 reset (como CreateLoad)
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                              handlingEquipmentError: false,
                              handlingEquipmentOtherError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption?.label || "",
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              locationError: false,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          }

                          setPickUpPlaces(updated);
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={place.locationError ? "edit-load-select-error" : "edit-load-select"}
                      />
                    </div>
                  </div>

                  {place.locationError && <p className="edit-load-error-location">{t("errors.locationRequired")}</p>}

                  <div className="edit-load-datetime-group">
                    <div className="edit-load-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "date")}
                        onBlur={(e) => validateDateOnBlur(e.target.value, index, "pickup")}
                        className={`edit-load-input ${place.dateError || dateFieldErrors[`pickup-${index}`] ? "input-error" : ""}`}
                        ref={(el) => {
                          if (el) dateRefs.current[index] = el;
                        }}
                      />
                      <FaCalendarAlt
                        className="edit-load-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = dateRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>

                    <div className="edit-load-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder="HH:MM"
                        value={place.hour || ""}
                        onChange={(e) => handleHourInput(e, index, "hour", true)}
                        className={`edit-load-input ${
                          invalidHourFields.some((f) => f.type === "pickup" && f.index === index) || place.hourError
                            ? "input-error"
                            : ""
                        }`}
                        ref={(el) => {
                          if (el) hourRefs.current[index] = el;
                        }}
                      />
                      <FaClock
                        className="edit-load-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = hourRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="edit-load-point-dimensions-block">
                    <label className="edit-load-label edit-load-point-dimensions-title">
                      {t("fields.pointDimensions", {
                        defaultValue: "Dimensions of this point",
                      })}
                    </label>

                    <div className="edit-load-point-dimensions-row">
                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointLength", { defaultValue: "Length (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "length")}
                          className={`edit-load-input ${place.pointLengthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointLengthErrorMessage && (
                          <p className="edit-load-error">{place.pointLengthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointWidth", { defaultValue: "Width (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "width")}
                          className={`edit-load-input ${place.pointWidthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointWidthErrorMessage && (
                          <p className="edit-load-error">{place.pointWidthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointHeight", { defaultValue: "Height (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "height")}
                          className={`edit-load-input ${place.pointHeightError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointHeightErrorMessage && (
                          <p className="edit-load-error">{place.pointHeightErrorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {place.dateError && <p className="edit-load-error-data">{t("errors.dateRequired")}</p>}

                  {place.hourError && (
                    <>
                      {!place.hour?.trim() ? (
                        <p className="edit-load-error-data">{t("errors.hourRequired")}</p>
                      ) : (
                        <p className="edit-load-error">{t("errors.invalidHourFormat")}</p>
                      )}
                    </>
                  )}

                  {invalidHourFields.some((f) => f.type === "pickup" && f.index === index) && (
                    <p className="edit-load-error">{t("errors.invalidHourFormat")}</p>
                  )}

                  {/* ✅ Loading Access */}
                  <label className="edit-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={place.loadingAccessError ? "edit-load-select-error" : "edit-load-select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {/* ✅ Handling Equipment */}
                  <label className="edit-load-label">
                    {t("fields.handlingEquipment", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                      isClearable
                      placeholder={t("fields.handlingEquipmentPlaceholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={place.handlingEquipmentError ? "edit-load-select-error" : "edit-load-select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").toUpperCase() === "OTHER" && (
                    <div className="edit-load-field-group" style={{ marginBottom: 10 }}>
                      <label className="edit-load-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => handleHandlingEquipmentOtherChange(index, e, true)}
                        className={`edit-load-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                      />
                    </div>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <div className="edit-load-field-group" style={{ marginBottom: 10 }}>
                    <label className="edit-load-label">
                      {t("reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("referencePlaceholder", {
                        defaultValue: "e.g.: PO 12345, customer ref...",
                      })}
                      value={place.reference || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPickUpPlaces((prev) => {
                          const updated = [...prev];
                          if (!updated[index]) return prev;
                          updated[index] = { ...updated[index], reference: v };
                          return updated;
                        });
                      }}
                      className="edit-load-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <div className="edit-load-pickup-extra-fields">
                        <input
                          type="text"
                          placeholder={t("place.address")}
                          value={place.address || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "address")}
                          className={`edit-load-input edit-load-main-field ${place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-load-row-compact">
                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.postalCode")}
                              value={place.postalCode || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "postalCode")}
                              className={`edit-load-input edit-load-main-field ${place.postalCodeError ? "input-error" : ""}`}
                            />
                          </div>

                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.city")}
                              value={place.city || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "city")}
                              className={`edit-load-input edit-load-main-field ${place.cityError ? "input-error" : ""}`}
                            />
                          </div>

                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.country")}
                              value={place.country || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "country")}
                              className={`edit-load-input edit-load-main-field ${place.countryError ? "input-error" : ""}`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("place.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "contactPhone")}
                          className={`edit-load-input edit-load-main-field ${place.contactPhoneError ? "input-error" : ""}`}
                        />

                        <input
                          type="text"
                          placeholder={t("place.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "gMapsLink")}
                          className={`edit-load-input edit-load-main-field ${place.gMapsLinkError ? "input-error" : ""}`}
                        />

                        <input
                          type="text"
                          placeholder={t("place.coordinates")}
                          value={place.coordinates || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "coordinates")}
                          className={`edit-load-input edit-load-main-field ${place.coordinatesError ? "input-error" : ""}`}
                        />
                      </div>

                      <div className="edit-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-load-toggle-fields-btn"
                          onClick={() => {
                            setPickUpPlaces((prev) => {
                              const updated = [...prev];
                              if (updated[index]) updated[index].showMoreFields = false;
                              return updated;
                            });
                          }}
                        >
                          {t("actions.hideFields")} <FiChevronUp className="edit-load-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-load-toggle-fields-btn"
                        onClick={() => {
                          setPickUpPlaces((prev) => {
                            const updated = [...prev];
                            if (updated[index]) updated[index].showMoreFields = true;
                            return updated;
                          });
                        }}
                      >
                        {t("actions.moreFields")} <FiChevronDown className="edit-load-toggle-arrow" />
                      </button>
                    </div>
                  )}

                  {dateFieldErrors[`pickup-${index}`] && <p className="edit-load-error">{dateFieldErrors[`pickup-${index}`]}</p>}
                </div>
              ))}

              {pickUpPlaces.length < MAX_POINTS ? (
                <button type="button" className="edit-load-add-location-btn" onClick={addPickUpPlace}>
                  {t("actions.addPickup")}
                </button>
              ) : (
                <p className="edit-load-error-max-points">{t("errors.maxPickups")}</p>
              )}
            </div>

            {/* Column 2 - Merchandise */}
            <div className="edit-load-column">
              <h3 className="edit-load-column-title">{t("columns.merchandise")}</h3>

              <div className="edit-load-merchandise-inline-row">
                <div className="edit-load-field-group edit-load-weight-field">
                  <label className="edit-load-label">
                    {t("labels.weight", { defaultValue: "Weight (kg)" })}
                  </label>

                  <input
                    type="number"
                    value={weight}
                    onChange={handleWeightChange}
                    className={`edit-load-input ${weightError ? "input-error" : ""}`}
                    step="0.01"
                    min="0"
                    placeholder="0"
                  />

                  {weightError && <p className="edit-load-error">{weightError}</p>}
                </div>

                <div className="edit-load-field-group edit-load-merchandise-field">
                  <label className="edit-load-label">
                    {t("fields.merchandise", { defaultValue: "Merchandise*" })}
                  </label>

                  <input
                    type="text"
                    value={merchandise}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMerchandise(v);
                      if (merchandiseError && v.trim()) setMerchandiseError("");
                    }}
                    placeholder={t("fields.merchandisePlaceholder", {
                      defaultValue: "e.g. Pallets, Food, Furniture...",
                    })}
                    className={`edit-load-input ${merchandiseError ? "input-error" : ""}`}
                  />

                  {merchandiseError && <p className="edit-load-error">{merchandiseError}</p>}
                </div>
              </div>

              <div className="edit-load-load-dimensions-summary">
                <label className="edit-load-label edit-load-load-dimensions-title">
                  {t("fields.loadDimensions", {
                    defaultValue: "Total load dimensions",
                  })}
                </label>

                <div className="edit-load-load-dimensions-row">
                  <div className="edit-load-load-dimension-field">
                    <label>{t("labels.length", { defaultValue: "Length (m)" })}</label>
                    <input
                      type="text"
                      value={length}
                      readOnly
                      className="edit-load-input edit-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-load-load-dimension-field">
                    <label>{t("labels.width", { defaultValue: "Width (m)" })}</label>
                    <input
                      type="text"
                      value={width}
                      readOnly
                      className="edit-load-input edit-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-load-load-dimension-field">
                    <label>{t("labels.height", { defaultValue: "Height (m)" })}</label>
                    <input
                      type="text"
                      value={height}
                      readOnly
                      className="edit-load-input edit-load-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="edit-load-dimension-warning-text">
                    {t("errors.pickupDeliveryLengthMismatch", {
                      defaultValue:
                        "⚠️ The sum of pickup and delivery lengths still does not match.",
                    })}
                  </p>
                )}

                <div className="edit-load-dimension-helper-text">
                  {t("fields.loadDimensionsHelper", {
                    defaultValue:
                      "Length is calculated from the sum of the points. Width and height use the highest registered value.",
                  })}
                </div>
              </div>

                {/* 🆕 Trailer Type */}
                <div className="edit-load-field-group">
                  <label className="edit-load-label">
                    {t("fields.trailerType.label", { defaultValue: "Trailer type" })}
                  </label>

                  <Select
                    options={trailerTypeOptions}
                    value={trailerTypeOptions.find((o) => o.value === trailerType) || null}
                    onChange={(opt) => {
                      const v = opt?.value ? String(opt.value).trim().toUpperCase() : null;

                      setTrailerType(v);

                      // ✅ if not OTHER, clear free text
                      if (v !== "OTHER") setTrailerTypeOther("");

                      if (trailerTypeError) setTrailerTypeError("");
                      if (trailerTypeOtherError) setTrailerTypeOtherError("");
                    }}
                    isClearable
                    placeholder={t("fields.trailerType.placeholder", {
                      defaultValue: "Select trailer type...",
                    })}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={trailerTypeError ? "edit-load-select-error" : "edit-load-select"}
                    menuPortalTarget={document.body}
                  />

                  {trailerTypeError && <p className="edit-load-error">{trailerTypeError}</p>}
                </div>

                {trailerType === "OTHER" && (
                  <div className="edit-load-field-group" style={{ marginTop: 3 }}>
                    <label className="edit-load-label">
                      {t("fields.trailerTypeOther.label", { defaultValue: "Other (specify)" })}
                    </label>

                    <input
                      type="text"
                      value={trailerTypeOther || ""}
                      placeholder={t("fields.trailerTypeOther.placeholder", {
                        defaultValue: "Write the trailer type...",
                      })}
                      onChange={(e) => {
                        setTrailerTypeOther(e.target.value);
                        if (trailerTypeOtherError) setTrailerTypeOtherError("");
                      }}
                      className={`edit-load-input edit-load-trailer-type-other-input ${trailerTypeOtherError ? "input-error" : ""}`}
                    />

                    {trailerTypeOtherError && <p className="edit-load-error">{trailerTypeOtherError}</p>}
                  </div>
                )}

              <h3 className="edit-load-general-info-title">{t("sections.generalInfo")}</h3>

              <div className="edit-load-field-group">
                <label className="edit-load-label">{t("labels.price")}</label>

                <div className={`edit-load-price-wrapper ${priceError ? "input-error" : ""}`}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={price ?? ""}
                    onChange={handlePriceChange}
                    className="edit-load-input edit-load-price-input"
                  />

                  <div className="edit-load-price-currency">
                    <Select
                      options={CURRENCY_OPTIONS}
                      value={CURRENCY_OPTIONS.find((o) => o.value === (priceCurrency || "EUR")) || CURRENCY_OPTIONS[0]}
                      onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                      isClearable={false}
                      isSearchable={false}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix="edit-load-select"
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                {priceError && <p className="edit-load-error">{priceError}</p>}
              </div>

              {/* 🆕 Price for Subcontracted (ONLY when external carrier) */}
              {showNotes4subcontracted && (
                <div className="edit-load-field-group">
                  <label className="edit-load-label">{t("fields.price4subcontracted.label")}</label>

                  <div className={`edit-load-price-wrapper ${price4subcontractedError ? "input-error" : ""}`}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t("fields.price4subcontracted.placeholder")}
                      value={price4subcontractedInput}
                      onChange={handlePrice4subcontractedChange}
                      className="edit-load-input edit-load-price-input"
                    />

                    <div className="edit-load-price-currency">
                      <Select
                        options={CURRENCY_OPTIONS}
                        value={CURRENCY_OPTIONS.find((o) => o.value === (priceCurrency || "EUR")) || CURRENCY_OPTIONS[0]}
                        onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                        isClearable={false}
                        isSearchable={false}
                        styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        classNamePrefix="edit-load-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  {price4subcontractedError && <p className="edit-load-error">{price4subcontractedError}</p>}
                </div>
              )}

              <div className="edit-load-field-group">
                <label className="edit-load-label">{t("labels.clientType")}</label>
                <Select
                  value={
                    clientType
                      ? { value: clientType, label: clientType === "industry" ? t("clientTypes.industry") : t("clientTypes.carrier") }
                      : null
                  }
                  onChange={(selected) => {
                    const type = selected?.value || "";
                    setClientType(type);
                    setClientTypeError("");
                    setSelectedClientContactEmails([]);

                    if (type === "industry") {
                      setIndustryClientId("");
                      setCarrierClientId("");
                    } else if (type === "carrier") {
                      setCarrierClientId("");
                      setIndustryClientId("");
                    } else {
                      setIndustryClientId("");
                      setCarrierClientId("");
                    }
                  }}
                  options={[
                    { value: "industry", label: t("clientTypes.industry") },
                    { value: "carrier", label: t("clientTypes.carrier") },
                  ]}
                  isClearable
                  isSearchable={false}
                  placeholder={t("place.selectClientType")}
                  styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  classNamePrefix={clientTypeError ? "edit-load-select-error" : "edit-load-select"}
                  menuPortalTarget={document.body}
                />
                {clientTypeError && <p className="edit-load-error">{clientTypeError}</p>}
              </div>

              {clientType && (
                <div className="edit-load-field-group">
                  <label className="edit-load-label">
                    {clientType === "industry" ? t("labels.industryClient") : t("labels.carrierClient")}
                  </label>

                  <Select
                    options={
                      clientType === "industry"
                        ? industryClients.length === 0 && industryClientPrefill
                          ? [industryClientPrefill]
                          : industryClients.map((c) => ({ value: c.id, label: c.companyName }))
                        : carrierClients.length === 0 && carrierClientPrefill
                        ? [carrierClientPrefill]
                        : carrierClients.map((c) => ({ value: c.id, label: c.companyName }))
                    }
                    value={
                      clientType === "industry"
                        ? industryClients.find((c) => c.id === industryClientId)
                          ? {
                              value: industryClientId,
                              label: industryClients.find((c) => c.id === industryClientId)?.companyName,
                            }
                          : industryClientPrefill || null
                        : carrierClients.find((c) => c.id === carrierClientId)
                        ? { value: carrierClientId, label: carrierClients.find((c) => c.id === carrierClientId)?.companyName }
                        : carrierClientPrefill || null
                    }
                    onChange={(selectedOption) => {
                      setSelectedClientContactEmails([]);

                      if (clientType === "industry") {
                        const val = selectedOption ? selectedOption.value : "";
                        setIndustryClientId(val);
                        setIndustryClientPrefill(selectedOption || null);
                      } else {
                        const val = selectedOption ? selectedOption.value : "";
                        setCarrierClientId(val);
                        setCarrierClientPrefill(selectedOption || null);
                      }
                      setClientSelectionError("");
                    }}
                    isClearable
                    placeholder={t("place.selectClient")}
                    styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    classNamePrefix={clientSelectionError ? "edit-load-select-error" : "edit-load-select"}
                    menuPortalTarget={document.body}
                  />
                  {clientSelectionError && <p className="edit-load-error">{clientSelectionError}</p>}

                  {((clientType === "industry" && industryClientId) || (clientType === "carrier" && carrierClientId)) && (
                    <div className="edit-load-field-group">
                      <label className="edit-load-label">
                        {t("fields.clientContactEmails.label", {
                          defaultValue: "Client contact email(s)",
                        })}
                      </label>

                      <Select
                        options={selectedClientContactEmailOptions}
                        value={selectedClientContactEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions) ? selectedOptions.slice(0, 3) : [];
                          setSelectedClientContactEmails(limited);
                        }}
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        placeholder={t("fields.clientContactEmails.placeholder", {
                          defaultValue: "Select up to 3 contact email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("fields.clientContactEmails.noOptions", {
                            defaultValue: "No contact emails found for this client.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="edit-load-select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("fields.clientContactEmails.helper", {
                          defaultValue: "These emails come from the client's contact list.",
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="edit-load-field-group">
                <label className="edit-load-label">{t("labels.transportCarrier")}</label>
                <Select
                  options={carrierOptionsStable}
                  value={selectedTransportOptMemo}
                  onChange={(opt) => {
                    setCarrierId(opt ? toId(opt.value) : "");
                    setTransportCarrierError("");
                  }}
                  isClearable
                  placeholder={t("place.selectCarrier")}
                  styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  classNamePrefix={transportCarrierError ? "edit-load-select-error" : "edit-load-select"}
                  menuPortalTarget={document.body}
                />
                {transportCarrierError && <p className="edit-load-error">{transportCarrierError}</p>}
              </div>

              {showNotes4subcontracted && (
                <div className="edit-load-field-group">
                  <label className="edit-load-label">
                    {t("fields.subcontractedEmail.label", {
                      defaultValue: "Subcontracted Emails",
                    })}
                  </label>

                  <Select
                    options={selectedSubcontractedEmailOptions}
                    value={selectedSubcontractedEmails}
                    onChange={(selectedOptions) => {
                      const limited = Array.isArray(selectedOptions) ? selectedOptions.slice(0, 3) : [];
                      setSelectedSubcontractedEmails(limited);
                    }}
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    placeholder={t("fields.subcontractedEmail.placeholder", {
                      defaultValue: "Select up to 3 subcontracted emails...",
                    })}
                    noOptionsMessage={() =>
                      t("fields.subcontractedEmail.noOptions", {
                        defaultValue: "No contact emails found for this subcontracted carrier.",
                      })
                    }
                    styles={multiSelectHorizontalStyles}
                    classNamePrefix="edit-load-select"
                    menuPortalTarget={document.body}
                  />

                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                    {t("fields.subcontractedEmail.helper", {
                      defaultValue:
                        "These emails come from the subcontracted carrier contact list. Maximum: 3.",
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Column 3 - Delivery Points */}
            <div className="edit-load-column">
              <h3 className="edit-load-column-title">{t("columns.unloadingPoints")}</h3>
              {deliveryPlaceError && <p className="edit-load-error">{deliveryPlaceError}</p>}

              {deliveryPlaces.map((place, index) => (
                <div key={index} className="edit-load-multi-location-input">
                  <div className="edit-load-label-row">
                    <label className="edit-load-points-label">{t("labels.deliveryPlace")}*</label>
                    {deliveryPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-load-delete-btn"
                        onClick={() => removeDeliveryPlace(index)}
                        title={t("tooltips.removeDelivery")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-load-input-row">
                    <div className="edit-load-select-wrapper">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        options={placeOptions}
                        placeholder={t("place.selectOrType")}
                        value={resolvePlaceSelectValue(place, placeOptions)}
                        formatOptionLabel={renderPlaceOption}
                        onChange={(selectedOption) => {
                          const updated = [...deliveryPlaces];

                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: null, // 🆕 reset (como CreateLoad)
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                              handlingEquipmentError: false,
                              handlingEquipmentOtherError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: String(p.id),
                              location: p.placeName,
                              locationError: false,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: null, // 🆕 reset (como CreateLoad)
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                              handlingEquipmentError: false,
                              handlingEquipmentOtherError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption?.label || "",
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: null, // 🆕 reset (como CreateLoad)
                              handlingEquipment: null,
                              handlingEquipmentOther: "",
                              handlingEquipmentError: false,
                              handlingEquipmentOtherError: false,
                              reference: updated[index]?.reference ?? "",
                            };
                          }

                          setDeliveryPlaces(updated);
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={place.locationError ? "edit-load-select-error" : "edit-load-select"}
                      />
                    </div>
                  </div>

                  {place.locationError && <p className="edit-load-error-location">{t("errors.locationRequired")}</p>}

                  <div className="edit-load-datetime-group">
                    <div className="edit-load-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "date")}
                        onBlur={(e) => validateDateOnBlur(e.target.value, index, "delivery")}
                        className={`edit-load-input ${place.dateError || dateFieldErrors[`delivery-${index}`] ? "input-error" : ""}`}
                        ref={(el) => {
                          if (el) deliveryDateRefs.current[index] = el;
                        }}
                      />
                      <FaCalendarAlt
                        className="edit-load-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = deliveryDateRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>

                    <div className="edit-load-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder="HH:MM"
                        value={place.hour || ""}
                        onChange={(e) => handleHourInput(e, index, "hour", false)}
                        className={`edit-load-input ${
                          invalidHourFields.some((f) => f.type === "delivery" && f.index === index) || place.hourError
                            ? "input-error"
                            : ""
                        }`}
                        ref={(el) => {
                          if (el) deliveryHourRefs.current[index] = el;
                        }}
                      />
                      <FaClock
                        className="edit-load-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = deliveryHourRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="edit-load-point-dimensions-block">
                    <label className="edit-load-label edit-load-point-dimensions-title">
                      {t("fields.pointDimensions", {
                        defaultValue: "Dimensions of this point",
                      })}*
                    </label>

                    <div className="edit-load-point-dimensions-row">
                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointLength", { defaultValue: "Length (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "length")}
                          className={`edit-load-input ${place.pointLengthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointLengthErrorMessage && (
                          <p className="edit-load-error">{place.pointLengthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointWidth", { defaultValue: "Width (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "width")}
                          className={`edit-load-input ${place.pointWidthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointWidthErrorMessage && (
                          <p className="edit-load-error">{place.pointWidthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-point-dimension-field">
                        <label>{t("fields.pointHeight", { defaultValue: "Height (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "height")}
                          className={`edit-load-input ${place.pointHeightError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointHeightErrorMessage && (
                          <p className="edit-load-error">{place.pointHeightErrorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {place.dateError && <p className="edit-load-error-data">{t("errors.dateRequired")}</p>}

                  {place.hourError && (
                    <>
                      {!place.hour?.trim() ? (
                        <p className="edit-load-error-data">{t("errors.hourRequired")}</p>
                      ) : (
                        <p className="edit-load-error">{t("errors.invalidHourFormat")}</p>
                      )}
                    </>
                  )}

                  {invalidHourFields.some((f) => f.type === "delivery" && f.index === index) && (
                    <p className="edit-load-error">{t("errors.invalidHourFormat")}</p>
                  )}

                  {/* ✅ Loading Access */} 
                  <label className="edit-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={place.loadingAccessError ? "edit-load-select-error" : "edit-load-select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {/* ✅ Handling Equipment */}
                  <label className="edit-load-label">
                    {t("fields.handlingEquipment", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                      isClearable
                      placeholder={t("fields.handlingEquipmentPlaceholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={place.handlingEquipmentError ? "edit-load-select-error" : "edit-load-select"}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").toUpperCase() === "OTHER" && (
                    <div className="edit-load-field-group" style={{ marginBottom: 10 }}>
                      <label className="edit-load-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", { defaultValue: "Write the equipment..." })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => handleHandlingEquipmentOtherChange(index, e, false)}
                        className={`edit-load-input ${place.handlingEquipmentOtherError ? "input-error" : ""}`}
                      />
                    </div>
                  )}

                  {/* 🆕 Reference (optional) */}
                  <div className="edit-load-field-group" style={{ marginBottom: 10 }}>
                    <label className="edit-load-label">
                      {t("reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("referencePlaceholder", {
                        defaultValue: "e.g.: PO 12345, customer ref...",
                      })}
                      value={place.reference || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDeliveryPlaces((prev) => {
                          const updated = [...prev];
                          if (!updated[index]) return prev;
                          updated[index] = { ...updated[index], reference: v };
                          return updated;
                        });
                      }}
                      className="edit-load-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <div className="edit-load-pickup-extra-fields">
                        <input
                          type="text"
                          placeholder={t("place.address")}
                          value={place.address || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "address")}
                          className={`edit-load-input edit-load-main-field ${place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-load-row-compact">
                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.postalCode")}
                              value={place.postalCode || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "postalCode")}
                              className={`edit-load-input edit-load-main-field ${place.postalCodeError ? "input-error" : ""}`}
                            />
                          </div>

                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.city")}
                              value={place.city || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "city")}
                              className={`edit-load-input edit-load-main-field ${place.cityError ? "input-error" : ""}`}
                            />
                          </div>

                          <div className="edit-load-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("place.country")}
                              value={place.country || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "country")}
                              className={`edit-load-input edit-load-main-field ${place.countryError ? "input-error" : ""}`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("place.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "contactPhone")}
                          className={`edit-load-input edit-load-main-field ${place.contactPhoneError ? "input-error" : ""}`}
                        />

                        <input
                          type="text"
                          placeholder={t("place.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "gMapsLink")}
                          className={`edit-load-input edit-load-main-field ${place.gMapsLinkError ? "input-error" : ""}`}
                        />

                        <input
                          type="text"
                          placeholder={t("place.coordinates")}
                          value={place.coordinates || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "coordinates")}
                          className={`edit-load-input edit-load-main-field ${place.coordinatesError ? "input-error" : ""}`}
                        />
                      </div>

                      <div className="edit-load-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-load-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...deliveryPlaces];
                            updated[index].showMoreFields = false;
                            setDeliveryPlaces(updated);
                          }}
                        >
                          {t("actions.hideFields")} <FiChevronUp className="edit-load-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-load-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPlaces];
                          updated[index].showMoreFields = true;
                          setDeliveryPlaces(updated);
                        }}
                      >
                        {t("actions.moreFields")} <FiChevronDown className="edit-load-toggle-arrow" />
                      </button>
                    </div>
                  )}

                  {dateFieldErrors[`delivery-${index}`] && <p className="edit-load-error">{dateFieldErrors[`delivery-${index}`]}</p>}
                  {deliveryBeforePickupErrors.date.includes(index) && <p className="edit-load-error">{t("errors.deliveryDateBeforePickup")}</p>}
                  {deliveryBeforePickupErrors.hour.includes(index) && !deliveryBeforePickupErrors.date.includes(index) && (
                    <p className="edit-load-error">{t("errors.deliveryHourBeforePickup")}</p>
                  )}
                </div>
              ))}

              {deliveryPlaces.length < MAX_POINTS ? (
                <button type="button" className="edit-load-add-location-btn" onClick={addDeliveryPlace}>
                  {t("actions.addDelivery")}
                </button>
              ) : (
                <p className="edit-load-error-max-points">{t("errors.maxDeliveries")}</p>
              )}
            </div>
          </div>

          {fieldValidationError.pickups?.length > 0 && <p className="edit-load-error pickup-error">{t("errors.allPickupsRequired")}</p>}
          {fieldValidationError.deliveries?.length > 0 && <p className="edit-load-error delivery-error">{t("errors.allDeliveriesRequired")}</p>}

          {showNotes4subcontracted && (
            <div className="edit-load-field-group">
              <label className="edit-load-label">
                {t("labels.notesForSubcontracted", { defaultValue: "Notes for subcontracted carrier" })}
              </label>

              <textarea
                rows={3}
                placeholder={t("place.notesSubcontractedPlaceholder", { defaultValue: "Write notes for the subcontracted carrier..." })}
                value={notes4subcontracted}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= 2000) {
                    setNotes4subcontracted(v);
                    if (notes4subcontractedError) setNotes4subcontractedError("");
                  } else {
                    setNotes4subcontractedError(t("errors.notesMax"));
                  }
                }}
                className={`edit-load-input ${notes4subcontractedError ? "input-error" : ""}`}
              />

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75 }}>
                <span>{t("labels.notesVisibleToSubcontracted", { defaultValue: "Visible to subcontracted carrier" })}</span>
                <span>{notes4subcontracted.length}/2000</span>
              </div>

              {notes4subcontractedError && <p className="edit-load-error">{notes4subcontractedError}</p>}
            </div>
          )}

          <div className="edit-load-field-group">
            <label className="edit-load-label">{t("labels.notesForDriver")}</label>
            <textarea
              rows={3}
              placeholder={t("place.notesPlaceholder")}
              value={notes4driver}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= 2000) {
                  setNotes4driver(v);
                  if (notes4driverError) setNotes4driverError("");
                } else {
                  setNotes4driverError(t("errors.notesMax"));
                }
              }}
              className={`edit-load-input ${notes4driverError ? "input-error" : ""}`}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75 }}>
              <span>{t("labels.notesVisibleInDriverApp")}</span>
              <span>{notes4driver.length}/2000</span>
            </div>
            {notes4driverError && <p className="edit-load-error">{notes4driverError}</p>}
          </div>

          {conflictError && <p className="edit-load-error">{conflictError}</p>}
          {hourValidationError && <p className="edit-load-error">{hourValidationError}</p>}

          <div className="edit-load-button-group">
            <button type="button" onClick={() => navigate("/loads")} className="edit-load-cancel-button">
              {t("actions.cancel")}
            </button>
            <button
              type="submit"
              className="edit-load-save-button"
              disabled={loading || !!weightError || !!priceError || !!merchandiseError || !!trailerTypeOtherError}
            >
              {loading ? t("actions.saving") : t("actions.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditNormalLoad;
